/**
 * PWAInstallGuide Component
 *
 * Guides users through installing the app as a PWA on their device.
 * Shows device-specific instructions for iOS and Android.
 */

"use client";

import { Button } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

type DeviceType = "ios" | "android" | null;

interface PWAInstallGuideProps {
  /** Additional CSS classes */
  className?: string;
  /** Callback when user closes the guide */
  onClose?: () => void;
}

/**
 * Auto-detect device type from user agent
 */
function detectDeviceType(): DeviceType {
  if (typeof navigator === "undefined") return null;

  const ua = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return "ios";
  if (/android/.test(ua)) return "android";
  return null;
}

const benefits = [
  {
    icon: "solar:bell-bold",
    colorClass: "bg-primary/20 text-primary",
    key: "notifications",
  },
  {
    icon: "solar:home-2-bold",
    colorClass: "bg-success/20 text-success",
    key: "homeScreen",
  },
  {
    icon: "solar:rocket-bold",
    colorClass: "bg-warning/20 text-warning",
    key: "faster",
  },
  {
    icon: "solar:full-screen-bold",
    colorClass: "bg-secondary/20 text-secondary",
    key: "fullscreen",
  },
];

export function PWAInstallGuide({
  className = "",
  onClose,
}: PWAInstallGuideProps) {
  const t = useTranslations("pwaInstall");
  const tCommon = useTranslations("common");
  const [deviceType, setDeviceType] = useState<DeviceType>(null);

  // Auto-detect device on mount (only for mobile devices)
  useEffect(() => {
    const detected = detectDeviceType();
    // Only auto-select if user is on iOS or Android
    // Desktop/web browser users need to manually select
    if (detected) {
      setDeviceType(detected);
    }
  }, []);

  const iosSteps = [
    {
      icon: "solar:share-bold",
      text: t("ios.step1"),
    },
    {
      icon: "solar:add-square-linear",
      text: t("ios.step2"),
    },
    {
      icon: "solar:check-circle-bold",
      text: t("ios.step3"),
    },
  ];

  const androidSteps = [
    {
      icon: "solar:menu-dots-bold",
      text: t("android.step1"),
    },
    {
      icon: "solar:smartphone-linear",
      text: t("android.step2"),
    },
    {
      icon: "solar:check-circle-bold",
      text: t("android.step3"),
    },
  ];

  const steps = deviceType === "ios" ? iosSteps : androidSteps;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Close button - only if onClose provided */}
      {onClose && (
        <div className="flex justify-end -mb-2">
          <Button
            isIconOnly
            variant="light"
            size="sm"
            onPress={onClose}
            aria-label={tCommon("close")}
            className="text-default-400 hover:text-foreground"
          >
            <Icon icon="solar:close-circle-linear" className="h-5 w-5" />
          </Button>
        </div>
      )}

      {/* Description */}
      <p className="text-sm text-default-500 leading-relaxed">
        {t("description")}
      </p>

      {/* Benefits Grid */}
      <div className="grid grid-cols-2 gap-3">
        {benefits.map((benefit) => (
          <div
            key={benefit.key}
            className="flex items-center gap-3 rounded-xl bg-content1 border border-divider p-3"
          >
            <div
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${benefit.colorClass}`}
            >
              <Icon
                icon={benefit.icon}
                className="h-4 w-4"
                aria-hidden="true"
              />
            </div>
            <span className="text-xs font-medium text-foreground leading-tight">
              {t(`benefits.${benefit.key}`)}
            </span>
          </div>
        ))}
      </div>

      {/* No device selected state */}

      <div className="flex flex-col items-center justify-center py-6 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-default-100 mb-3">
          <Icon
            icon="solar:hand-shake-linear"
            className="h-7 w-7 text-default-400"
            aria-hidden="true"
          />
        </div>
        <p className="text-sm text-default-400">{t("selectDevicePrompt")}</p>
      </div>

      {/* Device Selection */}
      <div className="grid grid-cols-2 gap-4">
        {/* iOS Button */}
        <button
          type="button"
          onClick={() => setDeviceType("ios")}
          className={`group relative flex flex-col items-center gap-3 rounded-xl p-5 transition-all duration-150 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
            deviceType === "ios"
              ? "bg-primary text-primary-foreground shadow-[inset_0_-4px_0_0_rgba(0,0,0,0.2)] active:shadow-[inset_0_2px_4px_0_rgba(0,0,0,0.2)] active:translate-y-0.5"
              : "bg-default-100 dark:bg-default-50 text-foreground shadow-[0_4px_0_0_hsl(var(--heroui-default-200))] dark:shadow-[0_4px_0_0_hsl(var(--heroui-default-100))] hover:bg-default-200 dark:hover:bg-default-100 active:shadow-none active:translate-y-1"
          }`}
          aria-pressed={deviceType === "ios"}
        >
          <Icon icon="ri:apple-fill" className="h-8 w-8" aria-hidden="true" />
          <span className="text-sm font-semibold">iPhone / iPad</span>
          {deviceType === "ios" && (
            <Icon
              icon="solar:check-circle-bold"
              className="absolute top-2 right-2 h-5 w-5"
              aria-hidden="true"
            />
          )}
        </button>

        {/* Android Button */}
        <button
          type="button"
          onClick={() => setDeviceType("android")}
          className={`group relative flex flex-col items-center gap-3 rounded-xl p-5 transition-all duration-150 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
            deviceType === "android"
              ? "bg-primary text-primary-foreground shadow-[inset_0_-4px_0_0_rgba(0,0,0,0.2)] active:shadow-[inset_0_2px_4px_0_rgba(0,0,0,0.2)] active:translate-y-0.5"
              : "bg-default-100 dark:bg-default-50 text-foreground shadow-[0_4px_0_0_hsl(var(--heroui-default-200))] dark:shadow-[0_4px_0_0_hsl(var(--heroui-default-100))] hover:bg-default-200 dark:hover:bg-default-100 active:shadow-none active:translate-y-1"
          }`}
          aria-pressed={deviceType === "android"}
        >
          <Icon icon="ri:android-fill" className="h-8 w-8" aria-hidden="true" />
          <span className="text-sm font-semibold">Android</span>
          {deviceType === "android" && (
            <Icon
              icon="solar:check-circle-bold"
              className="absolute top-2 right-2 h-5 w-5"
              aria-hidden="true"
            />
          )}
        </button>
      </div>

      {/* Instructions */}
      {deviceType && (
        <div className="space-y-4">
          {/* Steps Header */}
          <div className="flex items-center gap-2">
            <div className="h-px flex-1 bg-divider" />
            <span className="text-xs font-medium text-default-400 uppercase tracking-wider">
              {deviceType === "ios" ? t("ios.title") : t("android.title")}
            </span>
            <div className="h-px flex-1 bg-divider" />
          </div>

          {/* Step List */}
          <ol className="space-y-3">
            {steps.map((step, index) => (
              <li
                key={index}
                className="flex items-start gap-3 rounded-xl bg-content1 border border-divider p-4"
              >
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">
                  {index + 1}
                </div>
                <div className="flex items-center gap-2 pt-0.5 min-w-0">
                  <Icon
                    icon={step.icon}
                    className="h-5 w-5 text-default-400 shrink-0"
                    aria-hidden="true"
                  />
                  <span className="text-sm text-foreground leading-snug">
                    {step.text}
                  </span>
                </div>
              </li>
            ))}
          </ol>

          {/* Browser-specific note for iOS */}
          {deviceType === "ios" && (
            <div className="flex items-start gap-3 rounded-xl bg-warning/10 border border-warning/30 p-4">
              <Icon
                icon="solar:info-circle-bold"
                className="h-5 w-5 text-warning shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <p className="text-sm text-warning leading-snug">
                {t("ios.safariNote")}
              </p>
            </div>
          )}

          {/* Success message */}
          <div className="flex items-start gap-3 rounded-xl bg-success/10 border border-success/30 p-4">
            <Icon
              icon="solar:bell-bold"
              className="h-5 w-5 text-success shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <p className="text-sm text-success leading-snug">
              {t("successNote")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
